/**
 * <product-diagrams> — PDP section that lists every parts diagram the current
 * variant's SKU appears in, fetched from the external hotspot GraphQL API.
 *
 * The element stays hidden until at least one diagram comes back, and re-queries
 * whenever the shopper switches variant.
 */

const PRODUCT_DIAGRAMS_BASE_URL = '/pages/diagram/';
const DIAGRAM_FIELDS = 'assetId filename title manufacturer category url';

/** A part number must have some length and contain at least one digit. */
function looksLikePartNumber(value) {
  return !!value && value.length >= 3 && /\d/.test(value);
}

/**
 * Product handles end in the manufacturer part number for ~99% of the catalog
 * (e.g. polaris-filter-canister-r0517800 -> R0517800), which is what the
 * diagram API indexes. Used only when the variant SKU yields nothing.
 */
function handleTailSku(handle) {
  const tail = (handle || '').split('-').pop().toUpperCase();
  return looksLikePartNumber(tail) ? tail : '';
}

/**
 * Candidates derived from the Shopify variant SKU: the SKU as-is (some parts
 * are stored unprefixed, and a few diagram entries carry the prefix), plus the
 * SKU with its three-letter vendor code removed (HPP/PEN/JND/PLS/...).
 */
function variantSkuCandidates(sku) {
  const clean = (sku || '').trim().toUpperCase();
  if (!clean) return [];

  const candidates = [clean];
  const stripped = clean.slice(3);
  if (/^[A-Z]{3}/.test(clean) && looksLikePartNumber(stripped)) candidates.push(stripped);
  return candidates;
}

class ProductDiagrams extends HTMLElement {
  constructor() {
    super();
    this.cache = {};
    this.requestToken = 0;
    this.apiUrl = this.dataset.apiUrl;
    this.apiToken = this.dataset.apiToken;
    this.handleSku = handleTailSku(this.dataset.handle);
    this.designMode = this.dataset.designMode === 'true';
    this.track = this.querySelector('[data-diagrams-track]');
    this.countEl = this.querySelector('[data-diagrams-count]');
    this.prevBtn = this.querySelector('[data-diagrams-prev]');
    this.nextBtn = this.querySelector('[data-diagrams-next]');
    this.emptyEl = this.querySelector('[data-diagrams-empty]');
  }

  connectedCallback() {
    if (!this.apiUrl || !this.track) return;

    this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
    this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    window.addEventListener('resize', () => this.updateArrows());

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        this.load(variant ? variant.sku : '');
      });
    }

    this.load(this.dataset.sku || '');
  }

  disconnectedCallback() {
    if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
  }

  /* ---- Data ---- */

  /**
   * searchBySku is an exact, case-sensitive match on the *manufacturer* part
   * number, but Shopify SKUs usually carry a vendor prefix (HPPSPX4000FG vs
   * SPX4000FG). So we probe several candidates in one request via aliases.
   *
   * Variant-derived candidates win; the handle-derived one is only a fallback,
   * because a handle can't distinguish between variants.
   */
  query(sku) {
    const variantCandidates = variantSkuCandidates(sku);
    const candidates = variantCandidates.slice();
    if (this.handleSku && candidates.indexOf(this.handleSku) === -1) candidates.push(this.handleSku);
    if (candidates.length === 0) return Promise.resolve([]);

    const args = candidates.map((_, i) => `$sku${i}: String!`).join(', ');
    const aliases = candidates
      .map((_, i) => `r${i}: searchBySku(sku: $sku${i}) { ${DIAGRAM_FIELDS} }`)
      .join(' ');
    const variables = {};
    candidates.forEach((candidate, i) => {
      variables[`sku${i}`] = candidate;
    });

    return fetch(this.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.apiToken}`,
      },
      body: JSON.stringify({ query: `query SearchBySku(${args}) { ${aliases} }`, variables }),
    })
      .then((res) => res.json())
      .then((json) => {
        const data = json.data || {};
        const resultsFor = (list) =>
          list.reduce((acc, candidate) => acc.concat(data[`r${candidates.indexOf(candidate)}`] || []), []);

        const fromVariant = resultsFor(variantCandidates);
        return fromVariant.length > 0 ? fromVariant : resultsFor(candidates);
      });
  }

  load(rawSku) {
    const sku = (rawSku || '').trim();
    const token = ++this.requestToken;
    const cacheKey = sku || '__no-sku__';

    if (this.cache[cacheKey]) {
      this.render(this.cache[cacheKey]);
      return Promise.resolve();
    }

    return this.query(sku)
      .then((diagrams) => {
        this.cache[cacheKey] = diagrams;
        // Ignore responses that a newer variant selection has superseded.
        if (token !== this.requestToken) return;
        this.render(diagrams);
      })
      .catch((err) => {
        console.error('Failed to load diagrams for SKU', sku, err);
        if (token !== this.requestToken) return;
        this.render([]);
      });
  }

  /* ---- Rendering ---- */

  render(diagrams) {
    const items = this.dedupe(diagrams);

    if (items.length === 0) {
      this.track.innerHTML = '';
      // Keep the section visible in the theme editor so merchants can position it.
      this.hidden = !this.designMode;
      if (this.emptyEl) this.emptyEl.hidden = !this.designMode;
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    this.hidden = false;

    if (this.countEl) {
      this.countEl.textContent = `${items.length} diagram${items.length === 1 ? '' : 's'} include this part`;
    }

    this.track.innerHTML = items.map((item) => this.cardMarkup(item)).join('');
    this.track.scrollLeft = 0;
    this.updateArrows();
  }

  dedupe(diagrams) {
    const seen = {};
    return (diagrams || []).filter((item) => {
      if (!item || !item.assetId || seen[item.assetId]) return false;
      seen[item.assetId] = true;
      return true;
    });
  }

  cardMarkup(item) {
    const title = item.title || '';
    const manufacturer = item.manufacturer || '';

    const thumb = item.url
      ? `<img class="fpd-results__card-img" src="${this.escape(item.url)}" alt="${this.escape(
          `${title} parts diagram`
        )}" loading="lazy">`
      : '<svg class="fpd-results__card-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>';

    const tag = item.category
      ? `<span class="fpd-results__card-tag fpd-results__card-tag--${this.escape(
          item.category.toLowerCase().replace(/\s+/g, '-')
        )}">${this.escape(item.category)}</span>`
      : '';

    return (
      `<a class="fpd-results__card pdp-diagrams__card" href="${this.escape(this.diagramUrl(manufacturer, title))}">` +
      `<div class="fpd-results__card-thumb">${thumb}</div>` +
      '<div class="fpd-results__card-body">' +
      '<div class="fpd-results__card-header">' +
      `<span class="fpd-results__card-manufacturer">${this.escape(manufacturer)}</span>` +
      tag +
      '</div>' +
      `<p class="fpd-results__card-title">${this.escape(title)}</p>` +
      '</div>' +
      '<div class="fpd-results__card-footer">' +
      '<span class="fpd-results__card-cta">View Diagram &rsaquo;</span>' +
      '</div>' +
      '</a>'
    );
  }

  /* Mirrors the handle built by scripts/sync-diagrams.js for the diagram metaobjects. */
  diagramUrl(manufacturer, title) {
    const slug = `${manufacturer} ${title}`
      .replace(/^"|"$/g, '')
      .toLowerCase()
      .replace(/[“”‘’"']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 200);
    return PRODUCT_DIAGRAMS_BASE_URL + slug;
  }

  escape(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  /* ---- Scroller ---- */

  scrollByPage(direction) {
    const card = this.track.querySelector('.pdp-diagrams__card');
    const step = card ? card.offsetWidth + 16 : this.track.clientWidth;
    this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  updateArrows() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const scrollable = maxScroll > 1;
    this.classList.toggle('pdp-diagrams--scrollable', scrollable);
    this.prevBtn.disabled = !scrollable || this.track.scrollLeft <= 1;
    this.nextBtn.disabled = !scrollable || this.track.scrollLeft >= maxScroll - 1;
  }
}

if (!customElements.get('product-diagrams')) {
  customElements.define('product-diagrams', ProductDiagrams);
}
